import React from "react";
import WarningIcon from "@mui/icons-material/Warning";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { BLOCKING_REGULATION_TYPES } from "./PublishScheduleButton";

// One row per category, in display order. `label` is the row name in the
// tooltip; `count` formats the right-hand number. Availability conflicts are
// not a labor law rule, so they take the amber dot; everything else is rose.
const CATEGORIES = [
  {
    key: "weekly_overtime",
    label: "Overtime",
    count: (n) => `${n} ${n === 1 ? "person" : "people"}`,
  },
  { key: "daily_overtime", label: "Daily overtime" },
  { key: "seventh_day_overtime", label: "7th day" },
  { key: "availability", label: "Availability conflicts", tone: "amber" },
  { key: "shift_separation", label: "Clopens" },
  { key: "meal_break", label: "Meal breaks" },
  { key: "meal_break_timing", label: "Meal break timing" },
  { key: "rest_break", label: "Rest breaks" },
  { key: "rest_break_timing", label: "Rest break timing" },
  { key: "advance_schedule_notice", label: "Advance notice" },
  { key: "schedule_change_penalty", label: "Schedule changes" },
  { key: "minor_work_hours", label: "Minor work hours" },
  { key: "split_shift", label: "Split shifts" },
  { key: "other", label: "Other" },
];

const KNOWN_TYPES = CATEGORIES.map((c) => c.key);

function categoryKey(regulationType) {
  if (
    regulationType === "minor_work_hours_14_15" ||
    regulationType === "minor_work_hours_16_17"
  ) {
    return "minor_work_hours";
  }
  return KNOWN_TYPES.includes(regulationType) ? regulationType : "other";
}

function countByCategory(violations) {
  const counts = {};
  for (const v of violations) {
    const key = categoryKey(v.regulation_type);
    counts[key] = (counts[key] || 0) + 1;
  }
  return counts;
}

function toRows(counts) {
  return CATEGORIES.filter((c) => counts[c.key] > 0).map((c) => ({
    key: c.key,
    label: c.label,
    tone: c.tone || "rose",
    count: c.count ? c.count(counts[c.key]) : String(counts[c.key]),
  }));
}

export function formatDateRange(start, end) {
  if (!(start instanceof Date) || !(end instanceof Date)) return "";
  if (isNaN(start) || isNaN(end)) return "";
  const month = { month: "short" };
  const startLabel = `${start.toLocaleDateString("en-US", month)} ${start.getDate()}`;
  const endLabel =
    start.getMonth() === end.getMonth()
      ? String(end.getDate())
      : `${end.toLocaleDateString("en-US", month)} ${end.getDate()}`;
  return `${startLabel} – ${endLabel}`;
}

export const IssuesTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#ffffff",
    color: "#334155",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.10)",
    padding: 0,
    width: 280,
    maxWidth: 280,
  },
});

const DOT_COLOR = { rose: "#e11d48", amber: "#f59e0b" };

function Row({ row }) {
  return (
    <div className="flex items-center justify-between px-3.5 py-1.5 text-[13px]">
      <span className="flex items-center gap-2">
        <span
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: DOT_COLOR[row.tone] }}
        />
        {row.label}
      </span>
      <span className="font-semibold text-slate-900">{row.count}</span>
    </div>
  );
}

function GroupHeading({ children, color }) {
  return (
    <div
      className="px-3.5 pt-2 pb-0.5 text-[11px] font-semibold uppercase tracking-wide"
      style={{ color }}
    >
      {children}
    </div>
  );
}

// Rose pill that sits beside Publish. Same look at every width: the label
// shortens to the bare count where Publish itself shortens to "Publish".
// Hover lists each category with its count; click pins the affected
// employees to the top of the board.
function ScheduleIssuesPill(props) {
  const {
    countOfWarnings,
    laborLawEnabled,
    laborLawViolations,
    showWarnings,
    toggleShowWarnings,
    start_date,
    end_date,
    blockPublishOnLaborViolationsEnabled,
    roleType,
    roleTypeOptions,
    windowWidth,
  } = props;

  const availabilityCount = countOfWarnings?.countOfConflicts || 0;
  const violations = laborLawEnabled ? laborLawViolations || [] : [];

  const counts = laborLawEnabled
    ? countByCategory(violations)
    : { weekly_overtime: countOfWarnings?.countOfOvertime || 0 };
  counts.availability = availabilityCount;

  const rows = toRows(counts);
  const total = rows.reduce(
    (sum, row) => sum + (counts[row.key] || 0),
    0
  );
  if (!total) return null;

  // Same gate as the Publish button: only a full publish is checked up front.
  const isFullPublish =
    !roleType || !roleTypeOptions || roleType.length === roleTypeOptions.length;
  const isBlocking = (v) =>
    v.severity === "ERROR" && BLOCKING_REGULATION_TYPES.includes(v.regulation_type);
  const blockingViolations = violations.filter(isBlocking);
  const isBlocked =
    !!blockPublishOnLaborViolationsEnabled &&
    isFullPublish &&
    blockingViolations.length > 0;

  const compact = windowWidth <= 1279;
  const issuesLabel = `${total} ${total === 1 ? "issue" : "issues"}`;
  const label = isBlocked
    ? compact
      ? String(blockingViolations.length)
      : `${blockingViolations.length} blocking`
    : compact
    ? String(total)
    : issuesLabel;

  const dateRange = formatDateRange(start_date, end_date);

  let body;
  if (isBlocked) {
    const blockingRows = toRows(countByCategory(blockingViolations));
    const advisoryCounts = countByCategory(violations.filter((v) => !isBlocking(v)));
    advisoryCounts.availability = availabilityCount;
    const advisoryRows = toRows(advisoryCounts);
    body = (
      <>
        <GroupHeading color="#be123c">Blocks publishing</GroupHeading>
        {blockingRows.map((row) => (
          <Row key={row.key} row={{ ...row, tone: "rose" }} />
        ))}
        {advisoryRows.length > 0 && (
          <>
            <GroupHeading color="#b45309">Review before publishing</GroupHeading>
            {advisoryRows.map((row) => (
              <Row key={row.key} row={{ ...row, tone: "amber" }} />
            ))}
          </>
        )}
      </>
    );
  } else {
    body = rows.map((row) => <Row key={row.key} row={row} />);
  }

  const title = (
    <div>
      <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-slate-100">
        <span className="text-[13px] font-semibold text-slate-900">
          {total} schedule {total === 1 ? "issue" : "issues"}
        </span>
        {dateRange && <span className="text-[11px] text-slate-500">{dateRange}</span>}
      </div>
      <div className="py-1">{body}</div>
      <div className="px-3.5 py-2 border-t border-slate-100 text-[11px] text-slate-500">
        {showWarnings
          ? "Click to stop sorting affected employees to the top"
          : "Click to sort affected employees to the top"}
      </div>
    </div>
  );

  const buttonClassName = `
    inline-flex items-center gap-2 h-9 px-3.5 whitespace-nowrap
    rounded-lg border transition-all duration-200
    ${
      showWarnings || isBlocked
        ? "bg-rose-600 text-white border-rose-600 hover:bg-rose-700"
        : "bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100"
    }
  `;

  return (
    <IssuesTooltip title={title} placement="bottom-end">
      <button
        data-help-id="schedule-issues-pill"
        onClick={toggleShowWarnings}
        className={buttonClassName}
      >
        <WarningIcon style={{ fontSize: "16px" }} />
        <span className="text-[13px] font-medium">{label}</span>
      </button>
    </IssuesTooltip>
  );
}

export default ScheduleIssuesPill;
